import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { hubApi, PLAN_PRICING, STATUS_STYLE, calcFinalAmount } from "../api.js";

const fmt = d => d ? new Date(d).toLocaleDateString("ro-RO", { day:"2-digit", month:"short", year:"numeric" }) : "—";
const daysUntil = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

const C = {
  bg:"#f6f8fa", white:"#ffffff", border:"#e3e8ef",
  text:"#0a2540", muted:"#697386", subtle:"#a3acb9",
  indigo:"#635bff", indigoL:"#f0efff",
  green:"#09825d", greenL:"#e3f5ef",
  red:"#c0392b", redL:"#fdf2f2",
  amber:"#c07000", amberL:"#fff8e6",
};

export default function DashboardPage() {
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [search, setSearch]           = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan]   = useState("all");
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    hubApi.getAll()
      .then(({ data }) => setRestaurants(data.restaurants || []))
      .catch(() => toast.error("Nu s-au putut încărca restaurantele."))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (e, id) => {
    e.stopPropagation();
    try {
      const { data } = await hubApi.toggle(id);
      if (data.success) setRestaurants(r => r.map(x => x._id === id ? { ...x, isActive: data.isActive } : x));
    } catch { toast.error("Eroare."); }
  };

  const list = Array.isArray(restaurants) ? restaurants : [];

  /* metrici */
  const active  = list.filter(r => r.isActive);
  const mrr = active.reduce((sum, r) => {
    const base = calcFinalAmount(r.plan, r.billingPeriod||"monthly", r.discountType, r.discountValue);
    const m = r.billingPeriod==="yearly" ? base/12 : r.billingPeriod==="quarterly" ? base/3 : base;
    return sum + m;
  }, 0);
  const overdue  = list.filter(r => r.billingStatus === "overdue");
  const upcoming = list.filter(r => {
    if (!r.currentDueDate || r.billingStatus==="paid") return false;
    const d = daysUntil(r.currentDueDate);
    return d !== null && d >= 0 && d <= 30;
  }).sort((a,b) => new Date(a.currentDueDate)-new Date(b.currentDueDate));

  const filtered = list.filter(r => {
    const q = search.toLowerCase();
    if (q && !r.name.toLowerCase().includes(q) && !r.slug.toLowerCase().includes(q)) return false;
    if (filterStatus==="active"   && !r.isActive) return false;
    if (filterStatus==="inactive" && r.isActive)  return false;
    if (filterStatus==="overdue"  && r.billingStatus!=="overdue") return false;
    if (filterStatus==="pending"  && r.billingStatus!=="pending") return false;
    if (filterPlan!=="all" && String(r.plan)!==filterPlan) return false;
    return true;
  });

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;}
        input::placeholder{color:#a3acb9;}
        @keyframes spin{to{transform:rotate(360deg);}}
        .r-row:hover{background:#fafbff !important; cursor:pointer;}
        .act-btn:hover{background:#f0efff !important;}
        .bill-btn:hover{background:#635bff !important; color:#fff !important;}
      `}</style>

      {/* Top bar */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 32px", height:54, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:28, height:28, borderRadius:7, background:C.indigo, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <span style={{ fontWeight:700, fontSize:14, color:C.text }}>Orderly Hub</span>
        </div>
        <button onClick={()=>nav("/add")}
          style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:7, border:"none", background:C.indigo, color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Restaurant nou
        </button>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"28px 24px" }}>

        {/* KPI row */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:28 }}>
          <KpiCard label="Restaurante active" value={active.length} total={list.length} color={C.green} icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
          }/>
          <KpiCard label="MRR estimat" value={`€${Math.round(mrr).toLocaleString()}`} color={C.indigo} icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
          }/>
          <KpiCard label="ARR estimat" value={`€${Math.round(mrr*12).toLocaleString()}`} color={C.indigo} icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
          }/>
          <KpiCard label="Restanțe" value={overdue.length} color={overdue.length>0?C.red:C.green} icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          }/>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 260px", gap:20, alignItems:"start" }}>

          {/* LEFT — tabel restaurante */}
          <div>
            {/* Toolbar */}
            <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:14 }}>
              <div style={{ position:"relative", flex:1 }}>
                <svg style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.subtle} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Caută după nume sau slug..."
                  style={{ width:"100%", padding:"8px 12px 8px 34px", borderRadius:7, border:`1px solid ${C.border}`, background:C.white, fontSize:13, outline:"none", color:C.text }}/>
              </div>
              <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
                style={{ padding:"8px 12px", borderRadius:7, border:`1px solid ${C.border}`, background:C.white, fontSize:13, color:C.text, outline:"none", cursor:"pointer" }}>
                <option value="all">Toate statusurile</option>
                <option value="active">Activ</option>
                <option value="inactive">Inactiv</option>
                <option value="overdue">Restanță</option>
                <option value="pending">În așteptare</option>
              </select>
              <select value={filterPlan} onChange={e=>setFilterPlan(e.target.value)}
                style={{ padding:"8px 12px", borderRadius:7, border:`1px solid ${C.border}`, background:C.white, fontSize:13, color:C.text, outline:"none", cursor:"pointer" }}>
                <option value="all">Toate planurile</option>
                <option value="1">Light</option>
                <option value="2">Pro</option>
                <option value="3">Enterprise</option>
              </select>
            </div>

            {/* Tabel */}
            <div style={{ background:C.white, borderRadius:10, border:`1px solid ${C.border}`, overflow:"hidden" }}>
              {/* header */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 90px 110px 120px auto", gap:0, padding:"9px 16px", borderBottom:`1px solid ${C.border}`, background:C.bg }}>
                {["Restaurant","Plan","Status","Scadent","Sumă",""].map((h,i)=>(
                  <span key={i} style={{ fontSize:11, fontWeight:700, color:C.subtle, textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</span>
                ))}
              </div>

              {loading ? (
                <div style={{ padding:"40px 0", textAlign:"center", color:C.subtle, fontSize:13 }}>Se încarcă...</div>
              ) : filtered.length===0 ? (
                <div style={{ padding:"40px 0", textAlign:"center", color:C.subtle, fontSize:13 }}>
                  {list.length===0 ? "Niciun restaurant adăugat." : "Niciun rezultat pentru filtrele selectate."}
                </div>
              ) : filtered.map(r => {
                const plan   = PLAN_PRICING[r.plan] || PLAN_PRICING[1];
                const ss     = STATUS_STYLE[r.billingStatus] || STATUS_STYLE.pending;
                const days   = daysUntil(r.currentDueDate);
                const urgent = r.billingStatus!=="paid" && days!==null && days<=7;
                const amount = r.currentAmount ? `${Number(r.currentAmount).toLocaleString("ro-RO")} ${r.currentCurrency||"EUR"}` : "—";

                return (
                  <div key={r._id}
                    className="r-row"
                    onClick={()=>nav(`/restaurant/${r._id}`)}
                    style={{ display:"grid", gridTemplateColumns:"1fr 80px 90px 110px 120px auto", gap:0, padding:"13px 16px", borderBottom:`1px solid ${C.border}`, alignItems:"center", transition:"background 0.1s", background:C.white }}>

                    {/* Nume */}
                    <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
                      <div style={{ width:32, height:32, borderRadius:8, background:plan.bg, border:`1px solid ${plan.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:plan.color, flexShrink:0 }}>
                        {r.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ minWidth:0 }}>
                        <p style={{ margin:0, fontWeight:600, fontSize:13, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.name}</p>
                        <p style={{ margin:0, fontSize:11, color:C.subtle }}>{r.slug}</p>
                      </div>
                      {!r.isActive && <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:99, background:C.redL, color:C.red, border:`1px solid #f5c6cb`, flexShrink:0 }}>OFF</span>}
                    </div>

                    {/* Plan */}
                    <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:99, background:plan.bg, color:plan.color, border:`1px solid ${plan.border}`, whiteSpace:"nowrap", justifySelf:"start" }}>{plan.name}</span>

                    {/* Status billing */}
                    <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:99, background:ss.bg, color:ss.color, border:`1px solid ${ss.border}`, whiteSpace:"nowrap", justifySelf:"start" }}>{ss.label}</span>

                    {/* Scadent */}
                    <div>
                      <p style={{ margin:0, fontSize:12, color:urgent?C.red:C.text, fontWeight:urgent?700:400 }}>{fmt(r.currentDueDate)}</p>
                      {days!==null && r.billingStatus!=="paid" && (
                        <p style={{ margin:0, fontSize:10, color:days<0?C.red:days<=7?C.amber:C.subtle }}>
                          {days<0 ? `${Math.abs(days)}z restanță` : days===0 ? "azi" : `${days}z`}
                        </p>
                      )}
                    </div>

                    {/* Sumă */}
                    <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{amount}</span>

                    {/* Acțiuni */}
                    <div style={{ display:"flex", alignItems:"center", gap:6 }} onClick={e=>e.stopPropagation()}>
                      <button
                        className="bill-btn"
                        onClick={()=>nav(`/restaurant/${r._id}`)}
                        style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:6, border:`1px solid ${C.indigo}`, background:C.indigoL, color:C.indigo, fontSize:12, fontWeight:600, cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        Facturează
                      </button>
                      <button
                        className="act-btn"
                        onClick={e=>handleToggle(e, r._id)}
                        title={r.isActive?"Dezactivează":"Activează"}
                        style={{ width:30, height:30, borderRadius:6, border:`1px solid ${C.border}`, background:C.white, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"background 0.1s" }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={r.isActive?C.red:C.green} strokeWidth="2.5" strokeLinecap="round">
                          {r.isActive
                            ? <><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></>
                            : <><circle cx="12" cy="12" r="10"/><polyline points="9 11 12 14 22 4"/></>}
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Footer count */}
              {filtered.length>0 && (
                <div style={{ padding:"10px 16px", background:C.bg, borderTop:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:12, color:C.subtle }}>{filtered.length} restaurant{filtered.length!==1?"e":""}{filtered.length!==list.length?` din ${list.length}`:""}</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — upcoming + plan distrib */}
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

            {/* Plăți scadente curând */}
            <div style={{ background:C.white, borderRadius:10, border:`1px solid ${C.border}`, overflow:"hidden" }}>
              <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:13, fontWeight:700, color:C.text }}>Scadente în 30 zile</span>
                <span style={{ fontSize:11, fontWeight:700, padding:"1px 8px", borderRadius:99, background:upcoming.length>0?C.amberL:C.bg, color:upcoming.length>0?C.amber:C.subtle }}>{upcoming.length}</span>
              </div>
              {upcoming.length===0 ? (
                <p style={{ padding:"16px", fontSize:13, color:C.subtle, margin:0, textAlign:"center" }}>Nicio plată scadentă.</p>
              ) : upcoming.map(r=>{
                const days = daysUntil(r.currentDueDate);
                const plan = PLAN_PRICING[r.plan]||PLAN_PRICING[1];
                return (
                  <div key={r._id} onClick={()=>nav(`/restaurant/${r._id}`)}
                    style={{ padding:"11px 16px", borderBottom:`1px solid ${C.border}`, cursor:"pointer", transition:"background 0.1s" }}
                    onMouseEnter={e=>e.currentTarget.style.background=C.bg}
                    onMouseLeave={e=>e.currentTarget.style.background=""}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                      <div>
                        <p style={{ margin:0, fontSize:13, fontWeight:600, color:C.text }}>{r.name}</p>
                        <p style={{ margin:"2px 0 0", fontSize:11, color:C.subtle }}>{fmt(r.currentDueDate)}</p>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <p style={{ margin:0, fontSize:12, fontWeight:700, color:C.text }}>{r.currentAmount?`${r.currentAmount} ${r.currentCurrency||"EUR"}`:"—"}</p>
                        <span style={{ fontSize:10, fontWeight:700, color:days<=3?C.red:days<=7?C.amber:C.muted }}>
                          {days===0?"azi":days===1?"mâine":`${days} zile`}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Distribuție plan */}
            <div style={{ background:C.white, borderRadius:10, border:`1px solid ${C.border}`, padding:"16px" }}>
              <p style={{ fontSize:13, fontWeight:700, color:C.text, margin:"0 0 14px" }}>Distribuție plan</p>
              {[1,2,3].map(pid=>{
                const p    = PLAN_PRICING[pid];
                const cnt  = list.filter(r=>r.plan===pid).length;
                const pct  = list.length ? Math.round(cnt/list.length*100) : 0;
                return (
                  <div key={pid} style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:p.color }}>{p.name}</span>
                      <span style={{ fontSize:12, color:C.muted }}>{cnt} · {pct}%</span>
                    </div>
                    <div style={{ height:5, borderRadius:99, background:C.bg, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:p.color, borderRadius:99, transition:"width 0.5s" }}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Restanțe */}
            {overdue.length>0 && (
              <div style={{ background:C.redL, borderRadius:10, border:`1px solid #f5c6cb`, padding:"16px" }}>
                <p style={{ fontSize:13, fontWeight:700, color:C.red, margin:"0 0 10px", display:"flex", alignItems:"center", gap:6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Restanțe ({overdue.length})
                </p>
                {overdue.map(r=>(
                  <div key={r._id} onClick={()=>nav(`/restaurant/${r._id}`)}
                    style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid #f5c6cb`, cursor:"pointer" }}>
                    <span style={{ fontSize:12, color:C.red, fontWeight:500 }}>{r.name}</span>
                    <span style={{ fontSize:12, color:C.red, fontWeight:700 }}>{r.currentAmount?`${r.currentAmount} ${r.currentCurrency||"EUR"}`:"—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, total, color, icon }) {
  return (
    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e3e8ef", padding:"18px 20px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <span style={{ fontSize:12, fontWeight:600, color:"#697386" }}>{label}</span>
        <span style={{ color, opacity:0.7 }}>{icon}</span>
      </div>
      <p style={{ margin:0, fontSize:24, fontWeight:800, color:"#0a2540", letterSpacing:"-0.5px" }}>{value}</p>
      {total!=null && <p style={{ margin:"4px 0 0", fontSize:11, color:"#a3acb9" }}>din {total} total</p>}
    </div>
  );
}
